import { Stack, useTheme } from "@mui/material";
import { Banknote, Box } from "lucide-react";
import type { FC } from "react";
import { usePropertyDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyDocumentsQuery";
import {
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientPropertyCarVehicleTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CAR_SECURITY_TYPES } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/AutoMoto/carSecurityTypes";
import { CAR_SPECIAL_EQUIPMENT } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/AutoMoto/carSpecialEquipment";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import { UNITS } from "web_myplann/Shared/Utils/units";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";

const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyVehicleDetailResponseDTO;
	currencySign: string;
	propertyId?: number;
};

export const AutoMoto: FC<Props> = ({ data, currencySign, propertyId }) => {
	const { t } = useResource();
	const theme = useTheme();

	const { data: documents } = usePropertyDocumentsQuery(propertyId);

	const { properties: carSecurity } = useGetPropertyOptionalParams(
		CAR_SECURITY_TYPES,
		data,
		t,
	);
	const { properties: carSpecialEquipment } = useGetPropertyOptionalParams(
		CAR_SPECIAL_EQUIPMENT,
		data,
		t,
	);

	const carType =
		data.propertyType &&
		data.propertyType !== ClientPropertyVehicleTypeEnum.None
			? t(PageResources.Property.CarPropertyTypeValue[data.propertyType])
			: EMPTY_DETAIL_FIELD;

	const carBodyType =
		data.carBodyType && data.carBodyType !== ClientCarBodyTypeEnum.None
			? t(PageResources.Property.CarBodyTypeValue[data.carBodyType])
			: EMPTY_DETAIL_FIELD;

	const carFuel =
		data.carFuel && data.carFuel !== ClientCarFuelEnum.None
			? t(PageResources.Property.CarFuelTypeValue[data.carFuel])
			: EMPTY_DETAIL_FIELD;

	const carVehicleType =
		data.carVehicleType &&
		data.carVehicleType !== ClientPropertyCarVehicleTypeEnum.None
			? t(PageResources.Property.CarVehicleTypeValue[data.carVehicleType])
			: EMPTY_DETAIL_FIELD;

	const carBrand =
		data.carBrand || data.carModel
			? `${data.carBrand ?? ""} ${data.carModel ?? ""}`
			: EMPTY_DETAIL_FIELD;

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.CarPropertyType),
			value: carType,
		},
		{
			label: t(PageResources.Property.CarVehicleType),
			value:
				data.propertyType &&
				data.propertyType === ClientPropertyVehicleTypeEnum.OtherElectro
					? carVehicleType
					: null,
		},
		{
			label: t(PageResources.Property.CarLicensePlate),
			value: data.carLicensePlate,
		},
		{
			label: t(PageResources.Property.CarBrand),
			value: carBrand,
		},
		{
			label: t(PageResources.Property.CarVin),
			value: data.carVin,
		},
	];

	const carValueItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.Value),
			value: moneyFormatter(data.value, currencySign),
		},
	];

	const vehicleParametersItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.CarPower),
			value: unitFormatter(data.carPower, UNITS.kilowatt),
		},
		{
			label: t(PageResources.Property.CarVolume),
			value: unitFormatter(data.carVolume, UNITS.cubicCentimeter),
		},
		{
			label: t(PageResources.Property.CarBodyType),
			value: carBodyType,
		},
		{
			label: t(PageResources.Property.CarWeight),
			value: unitFormatter(data.carWeight, UNITS.kilogram),
		},
		{
			label: t(PageResources.Property.CarSeatCount),
			value: data.carSeatCount?.toLocaleString("cs-CZ"),
		},
		{
			label: t(PageResources.Property.CarFuelType),
			value: carFuel,
		},
		{
			type: "note",
			label: t(PageResources.Property.CarSecurity),
			text: carSecurity,
		},
		{
			type: "note",
			label: t(PageResources.Property.CarSpecialEquipment),
			text: carSpecialEquipment,
		},
	];

	const bottomComponent =
		data.carPurchaseDate ||
		data.carRegistration ||
		data.carManufactureMonthYear ||
		data.carPutIntoService ||
		data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.CarPurchaseDate)}
					value={dateFormatter(data.carPurchaseDate)}
				/>
				<DataRow
					label={t(PageResources.Property.CarRegistration)}
					value={dateFormatter(data.carRegistration)}
				/>
				<DataRow
					label={t(PageResources.Property.CreatedAt)}
					value={data.carManufactureMonthYear}
				/>
				<DataRow
					label={t(PageResources.Property.CarPutIntoService)}
					value={dateFormatter(data.carPutIntoService)}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
				items={basicInfoItems}
			/>

			<DataRowCard
				label={t(PageResources.Property.CarValue)}
				icon={
					<Banknote
						size={theme.iconSize.sm}
						color={theme.palette.deprecated.grey[700]}
					/>
				}
				items={carValueItems}
			/>

			<DataRowCard
				label={t(PageResources.Property.VehicleParameters)}
				icon={
					<Box
						size={theme.iconSize.sm}
						color={theme.palette.deprecated.grey[700]}
					/>
				}
				items={vehicleParametersItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
